import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, Edit2, MapPin, Search } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { fetchPostalCodesByCity } from '../services/postalCodeService';
import CityAutocompleteInput from './CityAutocompleteInput';

interface CreateTerritoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  businessId?: string;
}

interface Territory {
  id: string;
  name: string;
  postal_codes: string[];
}

export default function CreateTerritoriesModal({
  isOpen,
  onClose,
  onSuccess,
  businessId
}: CreateTerritoriesModalProps) {
  const { selectedBusiness } = useBusiness();
  const effectiveBusinessId = businessId || selectedBusiness?.id;
  const [isAnimating, setIsAnimating] = useState(false);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTerritory, setNewTerritory] = useState({
    name: '',
    postal_codes: ['']
  });
  const [editingTerritory, setEditingTerritory] = useState<Territory | null>(null);
  const [deletingTerritory, setDeletingTerritory] = useState<Territory | null>(null);
  
  // City-based postal code lookup
  const [entryMode, setEntryMode] = useState<'manual' | 'city'>('manual');
  const [cityInput, setCityInput] = useState({ city: '', state: '', country: 'US' as 'US' | 'CA' });
  const [cityInputValue, setCityInputValue] = useState('');
  const [fetchingPostalCodes, setFetchingPostalCodes] = useState(false);
  const [fetchedPostalCodes, setFetchedPostalCodes] = useState<string[]>([]);
  const [postalCodeError, setPostalCodeError] = useState<string | null>(null);

  const loadTerritories = async () => {
    if (!effectiveBusinessId || !supabase) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_areas')
        .select('*')
        .eq('business_id', effectiveBusinessId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTerritories(data || []);
    } catch (err) {
      console.error('Error loading territories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load territories');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadTerritories();
      // Add class to hide navigation header
      document.body.classList.add('modal-open-ios');
    } else {
      // Remove class to show navigation header
      document.body.classList.remove('modal-open-ios');
    }

    // Cleanup function to ensure class is removed
    return () => {
      document.body.classList.remove('modal-open-ios');
    };
  }, [isOpen]);

  const handleAddPostalCode = () => {
    setNewTerritory(prev => ({
      ...prev,
      postal_codes: [...prev.postal_codes, '']
    }));
  };

  const handleRemovePostalCode = (index: number) => {
    setNewTerritory(prev => ({
      ...prev,
      postal_codes: prev.postal_codes.filter((_, i) => i !== index)
    }));
  };

  const handlePostalCodeChange = (index: number, value: string) => {
    setNewTerritory(prev => ({
      ...prev,
      postal_codes: prev.postal_codes.map((code, i) => 
        i === index ? value : code
      )
    }));
  };

  const handleFetchPostalCodesWithFallback = async (
    primaryCity: string, 
    alternativeCities: string[], 
    state: string, 
    country: 'US' | 'CA'
  ) => {
    if (!primaryCity || !state) {
      setPostalCodeError('Please select a city');
      return;
    }

    setFetchingPostalCodes(true);
    setPostalCodeError(null);
    setFetchedPostalCodes([]);

    // Try primary city name first
    const citiesToTry = [primaryCity, ...alternativeCities.filter(c => c !== primaryCity)];
    
    for (const cityName of citiesToTry) {
      try {
        // Clean city name - remove common prefixes and extra whitespace
        const cleanedCity = cityName.trim().replace(/^(Metro|Greater|Greater Metro)\s+/i, '');
        
        const result = await fetchPostalCodesByCity(cleanedCity, state, country);

        if (result.success && result.postalCodes.length > 0) {
          setFetchedPostalCodes(result.postalCodes);
          setPostalCodeError(null);
          // Update city input with the working city name
          setCityInput(prev => ({ ...prev, city: cleanedCity }));
          // Auto-populate territory name with city name
          setNewTerritory(prev => ({ ...prev, name: `${cleanedCity}, ${state}` }));
          setFetchingPostalCodes(false);
          return;
        }
      } catch (error) {
        console.error(`Error fetching postal codes for ${cityName}:`, error);
        // Continue to next alternative
      }
    }

    // If all attempts failed
    setPostalCodeError(`No postal codes found for ${primaryCity}, ${state}. Try searching for a more specific city name.`);
    setFetchedPostalCodes([]);
    setFetchingPostalCodes(false);
  };

  const handleFetchPostalCodesFromCity = async (city: string, state: string, country: 'US' | 'CA') => {
    await handleFetchPostalCodesWithFallback(city, [], state, country);
  };

  const handleFetchPostalCodes = async () => {
    if (!cityInput.city || !cityInput.state) {
      setPostalCodeError('Please select a city from the dropdown');
      return;
    }

    await handleFetchPostalCodesFromCity(cityInput.city, cityInput.state, cityInput.country);
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveBusinessId || !supabase) return;

    try {
      setLoading(true);
      setError(null);

      // In city mode, use fetched postal codes and city name
      let validPostalCodes: string[] = [];
      let territoryName = newTerritory.name.trim();
      
      if (entryMode === 'city') {
        if (fetchedPostalCodes.length === 0) {
          throw new Error('Please fetch postal codes for a city first');
        }
        validPostalCodes = fetchedPostalCodes;
        
        // Use city name as territory name if not already set
        if (!territoryName && cityInput.city && cityInput.state) {
          territoryName = `${cityInput.city}, ${cityInput.state}`;
        }
      } else {
        // Manual mode - use postal codes from form
        validPostalCodes = newTerritory.postal_codes.filter(code => code.trim());
      }

      // Validate inputs
      if (!territoryName) {
        throw new Error('Territory name is required');
      }

      if (validPostalCodes.length === 0) {
        throw new Error('At least one postal code is required');
      }

      if (editingTerritory) {
        // Update existing territory
        const { error: updateError } = await supabase
          .from('service_areas')
          .update({
            name: territoryName,
            postal_codes: validPostalCodes
          })
          .eq('id', editingTerritory.id);

        if (updateError) throw updateError;
      } else {
        // Create territory
        const { error: createError } = await supabase
          .from('service_areas')
          .insert({
            business_id: effectiveBusinessId,
            name: territoryName,
            postal_codes: validPostalCodes
          });

        if (createError) throw createError;
      }

      // Reset form and reload territories
      setNewTerritory({ name: '', postal_codes: [''] });
      setEditingTerritory(null);
      setFetchedPostalCodes([]);
      setCityInputValue('');
      setCityInput({ city: '', state: '', country: 'US' });
      setPostalCodeError(null);

      await loadTerritories();

      // Notify parent component to refresh
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error creating/updating territory:', err);
      setError(err instanceof Error ? err.message : 'Failed to create/update territory');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTerritory = (territory: Territory) => {
    setEditingTerritory(territory);
    setNewTerritory({
      name: territory.name,
      postal_codes: territory.postal_codes.length > 0 ? territory.postal_codes : ['']
    });
  };

  const handleCancelEdit = () => {
    setEditingTerritory(null);
    setNewTerritory({ name: '', postal_codes: [''] });
  };

  const handleDeleteTerritory = async (territory: Territory) => {
    if (!effectiveBusinessId || !supabase) return;

    try {
      setLoading(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('service_areas')
        .delete()
        .eq('id', territory.id);

      if (deleteError) throw deleteError;

      // Close confirmation and reload territories
      setDeletingTerritory(null);
      await loadTerritories();
      
      // Notify parent component to refresh
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error deleting territory:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete territory');
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
      setNewTerritory({ name: '', postal_codes: [''] });
      setEditingTerritory(null);
      setDeletingTerritory(null);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return createPortal(
      <>
        {/* iOS-style backdrop with blur */}
        <div 
          className="fixed inset-0 transition-opacity duration-200"
          style={{
            zIndex: 100100,
            background: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            opacity: isOpen ? 1 : 0,
            pointerEvents: isOpen ? 'auto' : 'none'
          }}
          onClick={onClose}
        />
        
        {/* Modal - iOS Style */}
        <div className="fixed inset-0 flex items-center justify-center pointer-events-none sm:items-center" style={{ zIndex: 100110 }}>
        <div 
          className={`bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-2xl sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden transform transition-transform duration-300 ease-out ${
            isOpen ? 'translate-y-0' : 'translate-y-full sm:translate-y-0'
          }`}
          onTransitionEnd={handleTransitionEnd}
          onClick={e => e.stopPropagation()}
        >
          {/* Header - iOS Style */}
          <div 
            className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200/60"
            style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
          >
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">
              {editingTerritory ? 'Edit Territory' : 'Create New Territory'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gray-50/30">
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-[12px] text-sm">
                  {error}
                </div>
              )}

              {/* Create/Edit Territory Form */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-4 sm:p-6">
                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                    {/* Entry Mode Toggle */}
                    <div>
                      <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-[12px]">
                        <button
                          type="button"
                          onClick={() => {
                            setEntryMode('manual');
                            setFetchedPostalCodes([]);
                            setPostalCodeError(null);
                            setCityInputValue('');
                            setCityInput({ city: '', state: '', country: 'US' });
                          }}
                          className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all ${
                            entryMode === 'manual'
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          Manual Entry
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEntryMode('city');
                            setFetchedPostalCodes([]);
                            setPostalCodeError(null);
                            setCityInputValue('');
                            setCityInput({ city: '', state: '', country: 'US' });
                            // Clear territory name when switching to city mode
                            setNewTerritory(prev => ({ ...prev, name: '' }));
                          }}
                          className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all ${
                            entryMode === 'city'
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <MapPin className="h-3 w-3 inline mr-1" />
                          Add by City
                        </button>
                      </div>
                    </div>

                    {/* Territory Name - Only show in manual mode */}
                    {entryMode === 'manual' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Territory Name
                        </label>
                        <input
                          type="text"
                          value={newTerritory.name}
                          onChange={(e) => setNewTerritory(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full h-11 rounded-[12px] border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                          placeholder="Enter territory name"
                        />
                      </div>
                    )}

                    <div>
                      {entryMode === 'manual' ? (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Postal Codes
                            </label>
                            <button
                              type="button"
                              onClick={handleAddPostalCode}
                              className="text-sm font-medium text-blue-600 hover:text-blue-700 active:scale-95 transition-all touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              Add Another
                            </button>
                          </div>
                          <div className="space-y-3">
                            {newTerritory.postal_codes.map((code, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={code}
                                  onChange={(e) => handlePostalCodeChange(index, e.target.value)}
                                  className="flex-1 h-11 rounded-[12px] border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                                  placeholder="Enter postal code"
                                />
                                {newTerritory.postal_codes.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemovePostalCode(index)}
                                    className="p-2.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-[12px] active:scale-95 transition-all touch-manipulation"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="space-y-4">
                          {/* City Input Form */}
                          <div className="bg-blue-50 border border-blue-200 rounded-[12px] p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Search className="h-4 w-4 text-blue-600" />
                              <label className="text-sm font-medium text-gray-900">Find Postal Codes by City</label>
                            </div>
                            
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">City</label>
                                <CityAutocompleteInput
                                  value={cityInputValue}
                                  onChange={(value) => {
                                    setCityInputValue(value);
                                    // Clear city/state if user manually edits
                                    if (!value) {
                                      setCityInput(prev => ({ ...prev, city: '', state: '', country: 'US' }));
                                    }
                                  }}
                                  onCitySelected={(sel) => {
                                    setCityInput({ city: sel.city, state: sel.state, country: sel.country });
                                    setCityInputValue(sel.formatted);
                                    setFetchedPostalCodes([]);
                                    setPostalCodeError(null);
                                    handleFetchPostalCodesWithFallback(sel.city, sel.cityAlternatives, sel.state, sel.country);
                                  }}
                                  className="w-full h-10 rounded-[12px] border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                                  placeholder="Type city name (e.g., Toronto, New York, Los Angeles)"
                                />
                                <p className="mt-1.5 text-xs text-gray-500">
                                  Select a city from the dropdown suggestions
                                </p>
                              </div>
                              
                              {cityInput.city && cityInput.state ? (
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-[12px]">
                                    <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-gray-900 truncate">{cityInput.city}</p>
                                      <p className="text-xs text-gray-600">{cityInput.state}, {cityInput.country}</p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={handleFetchPostalCodes}
                                    disabled={fetchingPostalCodes}
                                    className="w-full px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-[12px] hover:bg-blue-700 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all touch-manipulation shadow-sm flex items-center justify-center gap-2"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    {fetchingPostalCodes ? (
                                      <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        <span>Searching...</span>
                                      </>
                                    ) : (
                                      <>
                                        <Search className="h-4 w-4" />
                                        <span>Find Postal Codes</span>
                                      </>
                                    )}
                                  </button>
                                </div>
                              ) : (
                                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-[12px] text-center">
                                  <p className="text-xs text-gray-500">
                                    Select a city from the dropdown above
                                  </p>
                                </div>
                              )}
                            </div>
                            
                            {postalCodeError && (
                              <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-[12px] text-xs">
                                {postalCodeError}
                              </div>
                            )}
                          </div>

                          {/* Fetched Postal Codes Preview */}
                          {fetchedPostalCodes.length > 0 && (
                            <div className="bg-green-50 border border-green-200 rounded-[12px] p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <MapPin className="h-4 w-4 text-green-600" />
                                <span className="text-sm font-medium text-gray-900">
                                  Found {fetchedPostalCodes.length} postal code{fetchedPostalCodes.length !== 1 ? 's' : ''} for {cityInput.city}, {cityInput.state}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                                {fetchedPostalCodes.map((code, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-white text-gray-700 border border-green-300"
                                  >
                                    {code}
                                  </span>
                                ))}
                              </div>
                              <p className="mt-3 text-xs text-gray-600">
                                Ready to create territory. Click "Create Territory" below to save.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-[12px] hover:bg-blue-700 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all touch-manipulation shadow-sm"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {loading ? 'Saving...' : editingTerritory ? 'Update Territory' : 'Create Territory'}
                      </button>
                      {editingTerritory && (
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-[12px] hover:bg-gray-50 active:scale-[0.98] transition-all touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>

              {/* Existing Territories Section */}
              {territories.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 sm:mb-4 px-1">Existing Territories</h3>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <div className="p-4 sm:p-6">
                      <div className="space-y-3">
                        {territories.map((territory) => (
                          <div key={territory.id} className="border border-gray-200 rounded-[12px] p-4 hover:border-gray-300 transition-colors bg-white">
                            <div className="flex justify-between items-start mb-3">
                              <h4 className="text-sm font-semibold text-gray-900">{territory.name}</h4>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleEditTerritory(territory)}
                                  className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-[12px] active:scale-95 transition-all touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                  title="Edit territory"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => setDeletingTerritory(territory)}
                                  className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-[12px] active:scale-95 transition-all touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                  title="Delete territory"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {territory.postal_codes.map((code, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                >
                                  {code}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deletingTerritory && (
        <div 
          className="fixed inset-0 flex items-center justify-center p-4" 
          style={{ 
            zIndex: 100120,
            background: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)'
          }}
          onClick={() => setDeletingTerritory(null)}
        >
          <div 
            className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 max-w-md w-full mx-4"
            style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'}}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-100 flex items-center justify-center">
                <Trash2 className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Territory</h3>
              <p className="text-base text-gray-600 mb-6">
                Are you sure you want to delete "<strong>{deletingTerritory.name}</strong>"? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingTerritory(null)}
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-[12px] hover:bg-gray-50 active:scale-[0.98] disabled:opacity-50 transition-all touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteTerritory(deletingTerritory)}
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-[12px] hover:bg-red-700 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all touch-manipulation shadow-sm"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </>,
    document.body
  );
}